import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  Loader2,
  Plus,
  Banknote,
  QrCode,
  ArrowRight,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { QtyStepper } from '@/components/pos/QtyStepper';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { EmptyState } from '@/components/pos/EmptyState';
import { MidtransQrisPanel } from '@/components/pos/PaymentDialog/MidtransQrisPanel';
import { usePaymentMethods } from '@/hooks/queries';
import { getProducts, resolveProductPrices } from '@/api/product.api';
import { getProductAvailability } from '@/api/ticketSchedule.api';
import { createAddOnSale, type CreateAddOnItem } from '@/api/wristband.api';
import { rupiah } from '@/lib/format';
import { SLOT_NONE, selectableSlots, slotTimeLabel } from '@/lib/slots';
import {
  CAP_REACHED_LABEL,
  buildCartCounts,
  buildHeldCounts,
  capOfProduct,
  capReachedSlotIds,
  findCapViolations,
  isCapped,
  maxAddableForProduct,
} from '@/lib/purchaseCap';
import type { WristbandAddOn } from '@/api/wristband.api';
import type { Product } from '@/types/product';
import type { Slot } from '@/types/product.slot';
import type { SaleStatus } from '@/types/sale';
import { todayInJakarta } from '@/lib/jakarta-date';
import { uuidv4 } from '@/lib/uuid';

type Stage = 'pick' | 'method' | 'cash' | 'qris';

interface QrisTxn {
  invoiceId: string;
  qrString: string | null;
  status: SaleStatus;
}

/**
 * RepurchaseAddOns — sell more add-ons against a scanned wristband.
 *
 * Cashier picks attraction add-ons (+ a time slot where required), then pays by
 * CASH (settles immediately in-session) or QRIS (Midtrans — QR shown here,
 * polled to settlement; the sync path appends the add-ons to the parent QR).
 * Either way the sale is attributed to the cashier for shift reporting.
 * E-money/cashless for repurchase is a follow-up (reader socket).
 */
export function RepurchaseAddOns({
  token,
  visitDate,
  wrongDate,
  heldAddOns = [],
  onBack,
  onPaid,
}: {
  token: string;
  visitDate: string | null;
  wrongDate: boolean;
  /**
   * The add-ons already on the scanned wristband. Feeds the purchase-cap gate:
   * live (non-refunded, unused) add-ons consume the same per-(attraction, slot)
   * quota this repurchase draws from.
   */
  heldAddOns?: WristbandAddOn[];
  onBack: () => void;
  onPaid: () => void | Promise<void>;
}) {
  const date = visitDate || todayInJakarta();
  // Per-unit slots: `slotIds[i]` is the chosen slot for unit i (length tracks qty).
  // A slot-required add-on bought in quantity N gets N independent slot picks —
  // mirrors the main-checkout per-seat "Waktu kunjungan" picker.
  const [lines, setLines] = useState<Record<string, { qty: number; slotIds: (string | null)[] }>>(
    {},
  );
  const [stage, setStage] = useState<Stage>('pick');
  const [tenderInput, setTenderInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [qris, setQris] = useState<QrisTxn | null>(null);
  const idemKeyRef = useRef<string>('');

  const { data: methods = [] } = usePaymentMethods();
  const cashMethod = useMemo(
    () => methods.find((m) => String(m.paymentGateway).toLowerCase() === 'cash'),
    [methods],
  );
  const qrisMethod = useMemo(
    () => methods.find((m) => String(m.paymentGateway).toLowerCase() === 'midtrans'),
    [methods],
  );

  // Addable products = active attraction tickets (the add-on catalog).
  const productsQuery = useQuery({
    queryKey: ['addon-products'],
    queryFn: async () => {
      const res = await getProducts({ per_page: 100, page_number: 1 });
      return res.items.filter(
        (p) => (p.ticketCategory ?? '').toLowerCase() === 'attraction' && p.status !== 'INACTIVE',
      );
    },
  });
  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data]);

  // Slot-aware prices for the visit date.
  const pricesQuery = useQuery({
    queryKey: ['addon-prices', date, products.map((p) => p.id).join(',')],
    enabled: products.length > 0,
    queryFn: () =>
      resolveProductPrices(
        products.map((p) => ({ productId: p.id })),
        date,
      ),
  });
  const priceOf = (p: Product) => pricesQuery.data?.prices?.[p.id]?.price ?? p.price ?? 0;

  const total = useMemo(
    () =>
      products.reduce((sum, p) => {
        const qty = lines[p.id]?.qty ?? 0;
        return sum + qty * priceOf(p);
      }, 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products, lines, pricesQuery.data],
  );

  const chosen = products.filter((p) => (lines[p.id]?.qty ?? 0) > 0);
  // Every unit of a slot-required add-on must have its own slot.
  const missingSlot = chosen.some(
    (p) => p.requiresSlot && (lines[p.id]?.slotIds ?? []).slice(0, lines[p.id]?.qty ?? 0).some((s) => !s),
  );

  // ── Purchase-cap gate (UX only; the /sale/addon path re-checks and 409s) ──
  // The quota is per ATTRACTION, so cart counts fold every picked product that
  // shares an attractionId. Held counts come from the wristband's live add-ons.
  const held = useMemo(() => buildHeldCounts(heldAddOns), [heldAddOns]);
  const cart = useMemo(
    () =>
      buildCartCounts(
        products
          .filter((p) => p.attractionId && (lines[p.id]?.qty ?? 0) > 0)
          .map((p) => ({
            attractionId: p.attractionId,
            slotIds: (lines[p.id]?.slotIds ?? []).slice(0, lines[p.id]?.qty ?? 0),
          })),
      ),
    [products, lines],
  );

  const setQty = (id: string, qty: number) =>
    setLines((cur) => {
      const prev = cur[id]?.slotIds ?? [];
      // Resize the per-unit slot array to the new qty, keeping earlier picks.
      const slotIds = Array.from({ length: qty }, (_, i) => prev[i] ?? null);
      return { ...cur, [id]: { qty, slotIds } };
    });
  const setUnitSlot = (id: string, unit: number, slotId: string | null) =>
    setLines((cur) => {
      const line = cur[id] ?? { qty: 1, slotIds: [null] };
      const slotIds = line.slotIds.slice();
      slotIds[unit] = slotId;
      return { ...cur, [id]: { ...line, slotIds } };
    });
  /** Copy unit #1's slot onto every other unit of this add-on (convenience). */
  const applyFirstSlotToAll = (id: string) =>
    setLines((cur) => {
      const line = cur[id];
      if (!line) return cur;
      const first = line.slotIds[0] ?? null;
      return { ...cur, [id]: { ...line, slotIds: line.slotIds.map(() => first) } };
    });

  /** Build the /sale/addon items[] from the picked lines (one slot per unit). */
  const buildAddOnItems = (): CreateAddOnItem[] =>
    chosen.map((p) => {
      const line = lines[p.id];
      const qty = line?.qty ?? 0;
      return {
        productId: p.id,
        quantity: qty,
        unitSchedules: Array.from({ length: qty }, (_, i) => ({
          slotId: p.requiresSlot ? (line?.slotIds[i] ?? null) : null,
        })),
      };
    });

  const goToMethod = () => {
    if (chosen.length === 0) return toast.warning('Pilih minimal satu add-on.');
    if (missingSlot) return toast.warning('Pilih jam untuk add-on yang membutuhkan slot.');
    // Backstop for a fan-out (e.g. "apply #1 to all") that the per-slot
    // disabling can't pre-empt: catch a cap breach before the payment sheet.
    const violations = findCapViolations({
      lines: chosen
        .filter((p) => p.attractionId)
        .map((p) => ({
          attractionId: p.attractionId,
          attractionName: p.name,
          cap: capOfProduct(p),
          slotIds: (lines[p.id]?.slotIds ?? []).slice(0, lines[p.id]?.qty ?? 0),
        })),
      held,
    });
    if (violations.length > 0) {
      return toast.warning(`${CAP_REACHED_LABEL} untuk sebagian jam. Kurangi jumlah atau ganti jam.`);
    }
    idemKeyRef.current = uuidv4();
    setStage('method');
  };

  const startCash = () => {
    if (!cashMethod) return toast.error('Metode tunai belum dikonfigurasi.');
    setTenderInput(String(total));
    setStage('cash');
  };

  const submitCash = async () => {
    if (!cashMethod) return;
    const tendered = Number(tenderInput) || 0;
    if (tendered < total) return toast.error('Uang tunai kurang dari total.');
    setSubmitting(true);
    try {
      await createAddOnSale(
        {
          qrToken: token,
          paymentMethodId: cashMethod.id,
          addOnItems: buildAddOnItems(),
          amountTendered: tendered,
        },
        idemKeyRef.current,
      );
      const change = Math.max(0, tendered - total);
      toast.success(change > 0 ? `Lunas. Kembalian ${rupiah(change)}.` : 'Lunas.');
      await onPaid();
    } catch (err) {
      toast.error((err as Error)?.message || 'Gagal memproses pembelian add-on.');
    } finally {
      setSubmitting(false);
    }
  };

  const startQris = async () => {
    if (!qrisMethod) return toast.error('Metode QRIS belum dikonfigurasi.');
    setSubmitting(true);
    try {
      const res = (await createAddOnSale(
        { qrToken: token, paymentMethodId: qrisMethod.id, addOnItems: buildAddOnItems() },
        idemKeyRef.current,
      )) as { invoiceId?: string; status?: SaleStatus; meta?: { qrString?: string; token?: string } };
      if (!res.invoiceId) throw new Error('Gagal membuat transaksi QRIS.');
      setQris({
        invoiceId: res.invoiceId,
        qrString: res.meta?.qrString ?? res.meta?.token ?? null,
        status: res.status ?? 'PENDING',
      });
      setStage('qris');
    } catch (err) {
      toast.error((err as Error)?.message || 'Gagal membuat transaksi QRIS.');
    } finally {
      setSubmitting(false);
    }
  };

  if (wrongDate) {
    return (
      <Card>
        <CardContent className="pt-6">
          <BackLink onBack={onBack} />
          <EmptyState
            icon={AlertCircle}
            title="Tidak tersedia"
            description="Add-on hanya dapat dibeli pada tanggal kunjungan gelang."
            className="border-0 bg-transparent"
          />
        </CardContent>
      </Card>
    );
  }

  // ── QRIS ─────────────────────────────────────────────────────────────────
  if (stage === 'qris' && qris) {
    return (
      <Card>
        <CardContent className="pt-6">
          <button
            type="button"
            onClick={() => setStage('method')}
            className="mb-2 flex items-center gap-1 text-xs font-medium text-crimson-700 hover:underline"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Metode lain
          </button>
          <MidtransQrisPanel
            total={total}
            invoiceId={qris.invoiceId}
            qrString={qris.qrString}
            status={qris.status}
            onStatus={(s) => setQris((cur) => (cur ? { ...cur, status: s } : cur))}
            onSuccess={onPaid}
            onCancel={() => setStage('method')}
          />
        </CardContent>
      </Card>
    );
  }

  // ── Method chooser ────────────────────────────────────────────────────────
  if (stage === 'method') {
    return (
      <Card>
        <CardContent className="space-y-5 pt-6">
          <button
            type="button"
            onClick={() => setStage('pick')}
            className="flex items-center gap-1 text-xs font-medium text-crimson-700 hover:underline"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Ubah pilihan
          </button>
          <div className="flex items-center justify-between rounded-xl bg-cream-100 px-4 py-3">
            <span className="text-sm font-medium text-ink-600">Total</span>
            <MoneyValue value={total} emphasis className="text-2xl" />
          </div>
          <p className="text-sm font-semibold text-ink-900">Pilih metode pembayaran</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Button
              size="lg"
              variant="outline"
              className="h-16 flex-col gap-1 text-base"
              disabled={!cashMethod || submitting}
              onClick={startCash}
            >
              <Banknote className="h-5 w-5" />
              Tunai
            </Button>
            <Button
              size="lg"
              className="h-16 flex-col gap-1 text-base"
              disabled={!qrisMethod || submitting}
              onClick={startQris}
            >
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <QrCode className="h-5 w-5" />}
              QRIS
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── Cash ──────────────────────────────────────────────────────────────────
  if (stage === 'cash') {
    const tendered = Number(tenderInput) || 0;
    const change = Math.max(0, tendered - total);
    return (
      <Card>
        <CardContent className="space-y-5 pt-6">
          <button
            type="button"
            onClick={() => setStage('method')}
            className="flex items-center gap-1 text-xs font-medium text-crimson-700 hover:underline"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Metode lain
          </button>
          <div className="flex items-center justify-between rounded-xl bg-cream-100 px-4 py-3">
            <span className="text-sm font-medium text-ink-600">Total</span>
            <MoneyValue value={total} emphasis className="text-2xl" />
          </div>

          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-400">
              <Banknote className="h-3.5 w-3.5" /> Uang tunai diterima
            </label>
            <div className="flex items-center rounded-xl border-2 border-cream-200 bg-white focus-within:border-crimson-600">
              <span className="pl-4 text-lg text-ink-400">Rp</span>
              <input
                autoFocus
                type="number"
                inputMode="numeric"
                min={total}
                value={tenderInput}
                onChange={(e) => setTenderInput(e.target.value)}
                className="tnum h-14 w-full bg-transparent px-3 font-display text-2xl font-bold text-ink-900 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {[total, Math.ceil(total / 50000) * 50000, Math.ceil(total / 100000) * 100000].map(
                (amt, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setTenderInput(String(amt))}
                    className="rounded-lg border border-cream-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:border-crimson-300"
                  >
                    {rupiah(amt)}
                  </button>
                ),
              )}
            </div>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-ink-500">Kembalian</span>
            <span className="tnum font-semibold text-green-700">{rupiah(change)}</span>
          </div>

          <Button
            size="lg"
            className="h-14 w-full gap-2 text-lg"
            disabled={submitting || tendered < total}
            onClick={submitCash}
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Banknote className="h-5 w-5" />}
            Bayar Tunai
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ── Pick ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <BackLink onBack={onBack} />
          <p className="mb-3 text-sm font-semibold text-ink-900">Pilih add-on</p>
          {productsQuery.isLoading ? (
            <div className="flex justify-center py-8 text-ink-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : products.length === 0 ? (
            <EmptyState
              icon={Plus}
              title="Tidak ada add-on"
              description="Belum ada produk add-on yang aktif untuk dijual."
              className="border-0 bg-transparent"
            />
          ) : (
            <div className="space-y-2">
              {products.map((p) => (
                <ProductRow
                  key={p.id}
                  product={p}
                  price={priceOf(p)}
                  qty={lines[p.id]?.qty ?? 0}
                  slotIds={lines[p.id]?.slotIds ?? []}
                  date={date}
                  held={held}
                  cart={cart}
                  onQty={(q) => setQty(p.id, q)}
                  onUnitSlot={(unit, s) => setUnitSlot(p.id, unit, s)}
                  onApplyFirstToAll={() => applyFirstSlotToAll(p.id)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between rounded-xl bg-white px-4 py-3 shadow-sm">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Total</p>
          <MoneyValue value={total} emphasis className="text-xl" />
        </div>
        <Button size="lg" className="h-12 gap-2" disabled={chosen.length === 0} onClick={goToMethod}>
          Lanjut ke pembayaran
          <ArrowRight className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="mb-3 flex items-center gap-1 text-xs font-medium text-crimson-700 hover:underline"
    >
      <ChevronLeft className="h-3.5 w-3.5" /> Kembali ke ringkasan
    </button>
  );
}

function ProductRow({
  product,
  price,
  qty,
  slotIds,
  date,
  held,
  cart,
  onQty,
  onUnitSlot,
  onApplyFirstToAll,
}: {
  product: Product;
  price: number;
  qty: number;
  slotIds: (string | null)[];
  date: string;
  held: Map<string, number>;
  cart: Map<string, number>;
  onQty: (q: number) => void;
  onUnitSlot: (unit: number, s: string | null) => void;
  onApplyFirstToAll: () => void;
}) {
  const needsSlot = !!product.requiresSlot && qty > 0;

  const availability = useQuery({
    queryKey: ['addon-availability', product.id, date],
    enabled: needsSlot,
    queryFn: () => getProductAvailability(product.id, date),
  });
  const slots: Slot[] = selectableSlots(availability.data?.slots ?? []);

  // Purchase-cap gate for this product's attraction. Absent/-1 → inert.
  const cap = capOfProduct(product);
  const capped = isCapped(cap);
  const ownSlotIds = slotIds.slice(0, qty);
  const blockedSlotIds = capped
    ? capReachedSlotIds({ cap, attractionId: product.attractionId, slots, held, cart })
    : new Set<string>();
  // Stepper ceiling: how many more this attraction can still take across all
  // its open slots (own units discounted so it's "where they could go").
  const maxQty = capped
    ? maxAddableForProduct({
        cap,
        attractionId: product.attractionId,
        slots,
        held,
        cart,
        ownSlotIds,
      })
    : Infinity;
  const stepperMax = Number.isFinite(maxQty) ? Math.min(20, Math.max(qty, maxQty)) : 20;

  return (
    <div className="rounded-xl border border-cream-200 bg-white px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-ink-900">{product.name}</p>
          <p className="tnum text-sm text-ink-400">{rupiah(price)}</p>
        </div>
        <QtyStepper value={qty} onChange={onQty} min={0} max={stepperMax} />
      </div>

      {needsSlot && (
        <div className="mt-3 border-t border-cream-100 pt-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-400">
              <Clock className="h-3.5 w-3.5" /> Waktu kunjungan {qty > 1 && `· ${qty} tiket`}
            </p>
            {qty > 1 && slots.length > 0 && (
              <button
                type="button"
                onClick={onApplyFirstToAll}
                className="text-xs font-medium text-crimson-700 hover:underline"
              >
                Terapkan tiket #1 ke semua
              </button>
            )}
          </div>
          {availability.isLoading ? (
            <div className="flex py-2 text-ink-400">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : slots.length === 0 ? (
            <p className="text-sm text-amber-600">Tidak ada slot tersedia untuk tanggal ini.</p>
          ) : (
            <div className="space-y-2">
              {Array.from({ length: qty }, (_, i) => {
                const value = slotIds[i] ?? null;
                return (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-16 shrink-0 text-sm font-medium text-ink-700">
                      Tiket #{i + 1}
                    </span>
                    <Select
                      value={value ?? SLOT_NONE}
                      onValueChange={(v) => onUnitSlot(i, v === SLOT_NONE ? null : v)}
                    >
                      <SelectTrigger className="flex-1" aria-label={`Waktu tiket ${i + 1}`}>
                        <SelectValue placeholder="Pilih waktu" />
                      </SelectTrigger>
                      <SelectContent>
                        {slots.map((s) => {
                          // Cap-blocked unless it's THIS unit's own current pick
                          // (a unit may always keep the slot it already holds).
                          const capBlocked = blockedSlotIds.has(s.id) && s.id !== value;
                          return (
                            <SelectItem key={s.id} value={s.id} disabled={capBlocked}>
                              {slotTimeLabel(s)}
                              {capBlocked ? ` · ${CAP_REACHED_LABEL}` : ` · Sisa ${s.availableCount}`}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {!value && (
                      <span className="flex shrink-0 items-center gap-1 text-xs text-amber-600">
                        <AlertCircle className="h-3 w-3" /> Wajib
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
